import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { FileText, Save, RefreshCw, Globe, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Package } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import { toast } from 'sonner';

export default function AdminContentSettings() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    site_name: 'NAMMAHUT',
    site_tagline: 'Our traditions come home',
  });

  // Landing Page Content
  const [landingContent, setLandingContent] = useState({
    welcome_text: 'வணக்கம் • स्वागतम् • Welcome',
    main_title: 'NammaHut',
    tagline: 'Explore Authentic Indian Experiences • Choose Your Journey',
    card1_title: 'NammaHut',
    card1_subtitle: 'Traditional Products',
    card1_description: 'Discover authentic Indian regional products, handcrafted treasures, and traditional delicacies',
    card2_title: 'NammaYatra',
    card2_subtitle: 'Traditional Travel Plans',
    card2_description: 'Experience authentic Indian culture with curated travel packages to heritage destinations',
    card3_title: 'International Delivery',
    card3_subtitle: 'Ship Worldwide',
    card3_description: 'Get authentic Indian products delivered to your doorstep anywhere in the world',
    tracking_title: 'Track Your Order',
    tracking_subtitle: 'Enter your order ID to check status',
  });

  // About Us Content
  const [aboutContent, setAboutContent] = useState({
    title: 'About Nammahut',
    subtitle: 'Bringing traditional South Indian heritage and authentic products to your doorstep',
    hero: '',
    mission_title: 'Our Mission',
    mission: '',
    story: '',
  });

  // Contact Us Content
  const [contactContent, setContactContent] = useState({
    title: 'Contact Us',
    email: 'support@nammahut.com',
    phone: '+91 (800) 123-4567',
    address: 'Karnataka, India',
    hours: '',
  });

  // Product Benefits
  const [benefitsContent, setBenefitsContent] = useState({
    benefit1_title: 'Free Shipping',
    benefit1_description: 'On orders over ₹500',
    benefit2_title: '30-Day Returns',
    benefit2_description: 'Easy return policy',
    benefit3_title: 'Secure Checkout',
    benefit3_description: '100% secure payments',
  });

  // Footer Content
  const [footerContent, setFooterContent] = useState({
    newsletter_title: 'Stay in the loop',
    newsletter_description: 'Get the latest updates on new products, exclusive offers, and special deals delivered straight to your inbox.',
    brand_description: 'Your trusted online marketplace for authentic traditional products. We bring you the best of traditional crafts and cultural heritage.',
    social_facebook: '#',
    social_twitter: '#',
    social_instagram: '#',
  });

  // Privacy Policy Content
  const [privacyContent, setPrivacyContent] = useState({
    title: 'Privacy Policy',
    content: '',
  });

  // Terms & Conditions Content
  const [termsContent, setTermsContent] = useState({
    title: 'Terms & Conditions',
    content: '',
  });

  useEffect(() => {
    fetchAllContent();
  }, []);

  const fetchAllContent = async () => {
    try {
      // Fetch settings
      const settingsRes = await axiosInstance.get(`${API_URL}/api/admin/settings`);
      if (settingsRes.data && Object.keys(settingsRes.data).length > 1) {
        setGeneralSettings({
          site_name: settingsRes.data.site_name || 'NAMMAHUT',
          site_tagline: settingsRes.data.site_tagline || 'Our traditions come home',
        });
        setContactContent(prev => ({
          ...prev,
          email: settingsRes.data.contact_email || prev.email,
          phone: settingsRes.data.contact_phone || prev.phone,
          address: settingsRes.data.contact_address || prev.address,
        }));
        setFooterContent(prev => ({
          ...prev,
          social_facebook: settingsRes.data.social_facebook || prev.social_facebook,
          social_twitter: settingsRes.data.social_twitter || prev.social_twitter,
          social_instagram: settingsRes.data.social_instagram || prev.social_instagram,
        }));
      }

      // Fetch content
      const landingRes = await axiosInstance.get(`${API_URL}/api/content/landing-page`);
      if (landingRes.data?.content) {
        setLandingContent(prev => ({ ...prev, ...landingRes.data.content }));
      }

      const aboutRes = await axiosInstance.get(`${API_URL}/api/content/about-us`);
      if (aboutRes.data?.content) {
        setAboutContent(prev => ({
          ...prev,
          title: aboutRes.data.title || prev.title,
          ...aboutRes.data.content,
        }));
      }

      const contactRes = await axiosInstance.get(`${API_URL}/api/content/contact-us`);
      if (contactRes.data?.content) {
        setContactContent(prev => ({
          ...prev,
          title: contactRes.data.title || prev.title,
          ...contactRes.data.content,
        }));
      }

      const benefitsRes = await axiosInstance.get(`${API_URL}/api/content/benefits`);
      if (benefitsRes.data?.content) {
        setBenefitsContent(prev => ({ ...prev, ...benefitsRes.data.content }));
      }

      const footerRes = await axiosInstance.get(`${API_URL}/api/content/footer`);
      if (footerRes.data?.content) {
        setFooterContent(prev => ({ ...prev, ...footerRes.data.content }));
      }

      const privacyRes = await axiosInstance.get(`${API_URL}/api/content/privacy-policy`);
      if (privacyRes.data) {
        setPrivacyContent({
          title: privacyRes.data.title || 'Privacy Policy',
          content: privacyRes.data.content?.text || '',
        });
      }

      const termsRes = await axiosInstance.get(`${API_URL}/api/content/terms-conditions`);
      if (termsRes.data) {
        setTermsContent({
          title: termsRes.data.title || 'Terms & Conditions',
          content: termsRes.data.content?.text || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
    }
  };

  const handleSaveGeneral = async () => {
    setLoading(true);
    try {
      await axiosInstance.post(`${API_URL}/api/admin/settings`, {
        site_name: generalSettings.site_name,
        site_tagline: generalSettings.site_tagline,
      });
      toast.success('General settings saved successfully');
    } catch (error) {
      toast.error('Failed to save general settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLanding = async () => {
    setLoading(true);
    try {
      await axiosInstance.post(`${API_URL}/api/content`, {
        page_name: 'landing-page',
        title: 'Landing Page',
        content: landingContent,
      });
      toast.success('Landing page content saved successfully');
    } catch (error) {
      toast.error('Failed to save landing page content');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAbout = async () => {
    setLoading(true);
    try {
      await axiosInstance.post(`${API_URL}/api/content`, {
        page_name: 'about-us',
        title: aboutContent.title,
        content: {
          subtitle: aboutContent.subtitle,
          hero: aboutContent.hero,
          mission_title: aboutContent.mission_title,
          mission: aboutContent.mission,
          story: aboutContent.story,
        },
      });
      toast.success('About Us content saved successfully');
    } catch (error) {
      toast.error('Failed to save About Us content');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async () => {
    setLoading(true);
    try {
      // Save to both content and settings
      await axiosInstance.post(`${API_URL}/api/content`, {
        page_name: 'contact-us',
        title: contactContent.title,
        content: {
          email: contactContent.email,
          phone: contactContent.phone,
          address: contactContent.address,
          hours: contactContent.hours,
        },
      });
      await axiosInstance.post(`${API_URL}/api/admin/settings`, {
        contact_email: contactContent.email,
        contact_phone: contactContent.phone,
        contact_address: contactContent.address,
      });
      toast.success('Contact information saved successfully');
    } catch (error) {
      toast.error('Failed to save contact information');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBenefits = async () => {
    setLoading(true);
    try {
      await axiosInstance.post(`${API_URL}/api/content`, {
        page_name: 'benefits',
        title: 'Product Benefits',
        content: benefitsContent,
      });
      toast.success('Product benefits saved successfully');
    } catch (error) {
      toast.error('Failed to save product benefits');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFooter = async () => {
    setLoading(true);
    try {
      await axiosInstance.post(`${API_URL}/api/content`, {
        page_name: 'footer',
        title: 'Footer Content',
        content: {
          newsletter_title: footerContent.newsletter_title,
          newsletter_description: footerContent.newsletter_description,
          brand_description: footerContent.brand_description,
        },
      });
      await axiosInstance.post(`${API_URL}/api/admin/settings`, {
        social_facebook: footerContent.social_facebook,
        social_twitter: footerContent.social_twitter,
        social_instagram: footerContent.social_instagram,
      });
      toast.success('Footer content saved successfully');
    } catch (error) {
      toast.error('Failed to save footer content');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrivacy = async () => {
    setLoading(true);
    try {
      await axiosInstance.post(`${API_URL}/api/content`, {
        page_name: 'privacy-policy',
        title: privacyContent.title,
        content: {
          text: privacyContent.content,
        },
      });
      toast.success('Privacy Policy saved successfully');
    } catch (error) {
      toast.error('Failed to save Privacy Policy');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTerms = async () => {
    setLoading(true);
    try {
      await axiosInstance.post(`${API_URL}/api/content`, {
        page_name: 'terms-conditions',
        title: termsContent.title,
        content: {
          text: termsContent.content,
        },
      });
      toast.success('Terms & Conditions saved successfully');
    } catch (error) {
      toast.error('Failed to save Terms & Conditions');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General Settings', icon: Globe },
    { id: 'landing', label: 'Landing Page', icon: FileText },
    { id: 'about', label: 'About Us', icon: FileText },
    { id: 'contact', label: 'Contact Info', icon: Mail },
    { id: 'benefits', label: 'Product Benefits', icon: Package },
    { id: 'footer', label: 'Footer', icon: FileText },
    { id: 'privacy', label: 'Privacy Policy', icon: FileText },
    { id: 'terms', label: 'Terms & Conditions', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Content & Settings</h1>
              <p className="text-gray-600 mt-2">Manage all website content and configuration in one place</p>
            </div>
            <Button
              onClick={fetchAllContent}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center space-x-2"
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </Button>
            );
          })}
        </div>

        {/* General Settings Tab */}
        {activeTab === 'general' && (
          <Card>
            <CardHeader className="bg-green-50">
              <CardTitle>General Website Settings</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Site Name</Label>
                  <Input
                    value={generalSettings.site_name}
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, site_name: e.target.value }))}
                    placeholder="e.g., NAMMAHUT"
                  />
                </div>
                <div>
                  <Label>Site Tagline</Label>
                  <Input
                    value={generalSettings.site_tagline}
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, site_tagline: e.target.value }))}
                    placeholder="e.g., Our traditions come home"
                  />
                </div>
              </div>
              <Button onClick={handleSaveGeneral} disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save General Settings
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Landing Page Tab */}
        {activeTab === 'landing' && (
          <Card>
            <CardHeader className="bg-green-50">
              <CardTitle>Landing Page Content</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Hero Section</h3>
                <div>
                  <Label>Welcome Text (Multi-language)</Label>
                  <Input
                    value={landingContent.welcome_text}
                    onChange={(e) => setLandingContent(prev => ({ ...prev, welcome_text: e.target.value }))}
                    placeholder="வணக்கம் • स्वागतम् • Welcome"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Main Title</Label>
                    <Input
                      value={landingContent.main_title}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, main_title: e.target.value }))}
                      placeholder="NammaHut"
                    />
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input
                      value={landingContent.tagline}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, tagline: e.target.value }))}
                      placeholder="Explore Authentic Indian Experiences"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Card 1: NammaHut</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={landingContent.card1_title}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, card1_title: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input
                      value={landingContent.card1_subtitle}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, card1_subtitle: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Label>Description</Label>
                    <Textarea
                      value={landingContent.card1_description}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, card1_description: e.target.value }))}
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Card 2: NammaYatra</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={landingContent.card2_title}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, card2_title: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input
                      value={landingContent.card2_subtitle}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, card2_subtitle: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Label>Description</Label>
                    <Textarea
                      value={landingContent.card2_description}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, card2_description: e.target.value }))}
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Card 3: International Delivery</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={landingContent.card3_title}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, card3_title: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input
                      value={landingContent.card3_subtitle}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, card3_subtitle: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Label>Description</Label>
                    <Textarea
                      value={landingContent.card3_description}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, card3_description: e.target.value }))}
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Order Tracking Section</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Tracking Title</Label>
                    <Input
                      value={landingContent.tracking_title}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, tracking_title: e.target.value }))}
                      placeholder="Track Your Order"
                    />
                  </div>
                  <div>
                    <Label>Tracking Subtitle</Label>
                    <Input
                      value={landingContent.tracking_subtitle}
                      onChange={(e) => setLandingContent(prev => ({ ...prev, tracking_subtitle: e.target.value }))}
                      placeholder="Enter your order ID to check status"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveLanding} disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Landing Page Content
              </Button>
            </CardContent>
          </Card>
        )}

        {/* About Us Tab */}
        {activeTab === 'about' && (
          <Card>
            <CardHeader className="bg-green-50">
              <CardTitle>About Us Page Content</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <Label>Page Title</Label>
                <Input
                  value={aboutContent.title}
                  onChange={(e) => setAboutContent(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="About Nammahut"
                />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input
                  value={aboutContent.subtitle}
                  onChange={(e) => setAboutContent(prev => ({ ...prev, subtitle: e.target.value }))}
                  placeholder="Brief description"
                />
              </div>
              <div>
                <Label>Hero Section</Label>
                <Textarea
                  value={aboutContent.hero || ''}
                  onChange={(e) => setAboutContent(prev => ({ ...prev, hero: e.target.value }))}
                  placeholder="Main introduction text..."
                  rows={4}
                />
              </div>
              <div>
                <Label>Mission Section Title</Label>
                <Input
                  value={aboutContent.mission_title}
                  onChange={(e) => setAboutContent(prev => ({ ...prev, mission_title: e.target.value }))}
                  placeholder="Our Mission"
                />
              </div>
              <div>
                <Label>Mission Content</Label>
                <Textarea
                  value={aboutContent.mission || ''}
                  onChange={(e) => setAboutContent(prev => ({ ...prev, mission: e.target.value }))}
                  placeholder="Describe your mission and values..."
                  rows={6}
                />
              </div>
              <div>
                <Label>Our Story</Label>
                <Textarea
                  value={aboutContent.story || ''}
                  onChange={(e) => setAboutContent(prev => ({ ...prev, story: e.target.value }))}
                  placeholder="Company story..."
                  rows={6}
                />
              </div>
              <Button onClick={handleSaveAbout} disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save About Us Content
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Contact Info Tab */}
        {activeTab === 'contact' && (
          <Card>
            <CardHeader className="bg-green-50">
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <Label>Page Title</Label>
                <Input
                  value={contactContent.title}
                  onChange={(e) => setContactContent(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Contact Us"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-email">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={contactContent.email}
                    onChange={(e) => setContactContent(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="support@nammahut.com"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-phone">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone Number
                  </Label>
                  <Input
                    id="contact-phone"
                    value={contactContent.phone}
                    onChange={(e) => setContactContent(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+91 (800) 123-4567"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="contact-address">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Address
                </Label>
                <Textarea
                  id="contact-address"
                  value={contactContent.address}
                  onChange={(e) => setContactContent(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Full address..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Business Hours</Label>
                <Textarea
                  value={contactContent.hours || ''}
                  onChange={(e) => setContactContent(prev => ({ ...prev, hours: e.target.value }))}
                  placeholder="Mon-Fri: 9am-6pm..."
                  rows={3}
                />
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Contact information is used across the site including footer, contact page, and other sections.
                </p>
              </div>
              <Button onClick={handleSaveContact} disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Contact Information
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Product Benefits Tab */}
        {activeTab === 'benefits' && (
          <Card>
            <CardHeader className="bg-green-50">
              <CardTitle>Product Benefits</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <p className="text-gray-600">Configure the benefits displayed on product pages and throughout the site.</p>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Benefit 1</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={benefitsContent.benefit1_title}
                      onChange={(e) => setBenefitsContent(prev => ({ ...prev, benefit1_title: e.target.value }))}
                      placeholder="Free Shipping"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={benefitsContent.benefit1_description}
                      onChange={(e) => setBenefitsContent(prev => ({ ...prev, benefit1_description: e.target.value }))}
                      placeholder="On orders over ₹500"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Benefit 2</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={benefitsContent.benefit2_title}
                      onChange={(e) => setBenefitsContent(prev => ({ ...prev, benefit2_title: e.target.value }))}
                      placeholder="30-Day Returns"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={benefitsContent.benefit2_description}
                      onChange={(e) => setBenefitsContent(prev => ({ ...prev, benefit2_description: e.target.value }))}
                      placeholder="Easy return policy"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Benefit 3</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={benefitsContent.benefit3_title}
                      onChange={(e) => setBenefitsContent(prev => ({ ...prev, benefit3_title: e.target.value }))}
                      placeholder="Secure Checkout"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={benefitsContent.benefit3_description}
                      onChange={(e) => setBenefitsContent(prev => ({ ...prev, benefit3_description: e.target.value }))}
                      placeholder="100% secure payments"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveBenefits} disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Product Benefits
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Footer Tab */}
        {activeTab === 'footer' && (
          <Card>
            <CardHeader className="bg-green-50">
              <CardTitle>Footer Content</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Newsletter Section</h3>
                <div>
                  <Label>Newsletter Title</Label>
                  <Input
                    value={footerContent.newsletter_title}
                    onChange={(e) => setFooterContent(prev => ({ ...prev, newsletter_title: e.target.value }))}
                    placeholder="Stay in the loop"
                  />
                </div>
                <div>
                  <Label>Newsletter Description</Label>
                  <Textarea
                    value={footerContent.newsletter_description}
                    onChange={(e) => setFooterContent(prev => ({ ...prev, newsletter_description: e.target.value }))}
                    placeholder="Newsletter description..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Brand Section</h3>
                <div>
                  <Label>Brand Description</Label>
                  <Textarea
                    value={footerContent.brand_description}
                    onChange={(e) => setFooterContent(prev => ({ ...prev, brand_description: e.target.value }))}
                    placeholder="About your brand..."
                    rows={4}
                  />
                </div>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Social Media Links</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>
                      <Facebook className="w-4 h-4 inline mr-2" />
                      Facebook URL
                    </Label>
                    <Input
                      value={footerContent.social_facebook}
                      onChange={(e) => setFooterContent(prev => ({ ...prev, social_facebook: e.target.value }))}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div>
                    <Label>
                      <Twitter className="w-4 h-4 inline mr-2" />
                      Twitter URL
                    </Label>
                    <Input
                      value={footerContent.social_twitter}
                      onChange={(e) => setFooterContent(prev => ({ ...prev, social_twitter: e.target.value }))}
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                  <div>
                    <Label>
                      <Instagram className="w-4 h-4 inline mr-2" />
                      Instagram URL
                    </Label>
                    <Input
                      value={footerContent.social_instagram}
                      onChange={(e) => setFooterContent(prev => ({ ...prev, social_instagram: e.target.value }))}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveFooter} disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Footer Content
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Privacy Policy Tab */}
        {activeTab === 'privacy' && (
          <Card>
            <CardHeader className="bg-green-50">
              <CardTitle>Privacy Policy</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Use a rich text editor or write your privacy policy content here. This will be displayed on the Privacy Policy page.
                </p>
              </div>
              <div>
                <Label>Page Title</Label>
                <Input
                  value={privacyContent.title}
                  onChange={(e) => setPrivacyContent(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Privacy Policy"
                />
              </div>
              <div>
                <Label>Privacy Policy Content</Label>
                <Textarea
                  value={privacyContent.content}
                  onChange={(e) => setPrivacyContent(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Enter your complete privacy policy content here..."
                  rows={20}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  You can use basic HTML tags for formatting (e.g., &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;)
                </p>
              </div>
              <Button onClick={handleSavePrivacy} disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Privacy Policy
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Terms & Conditions Tab */}
        {activeTab === 'terms' && (
          <Card>
            <CardHeader className="bg-green-50">
              <CardTitle>Terms & Conditions</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Write your terms and conditions content here. This will be displayed on the Terms & Conditions page.
                </p>
              </div>
              <div>
                <Label>Page Title</Label>
                <Input
                  value={termsContent.title}
                  onChange={(e) => setTermsContent(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Terms & Conditions"
                />
              </div>
              <div>
                <Label>Terms & Conditions Content</Label>
                <Textarea
                  value={termsContent.content}
                  onChange={(e) => setTermsContent(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Enter your complete terms and conditions content here..."
                  rows={20}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  You can use basic HTML tags for formatting (e.g., &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;)
                </p>
              </div>
              <Button onClick={handleSaveTerms} disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? <LoadingSpinner size="sm" className="mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Terms & Conditions
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
